import { type FC, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import type {
  CompanySubscriptionDetailsEntry,
  SubscriptionFeeSchedule,
  SubscriptionUsageBucket,
  UpcomingInvoicePreview,
} from "../../../../util/types";
import {
  PayableEntityType,
  PaymentsPricingMode,
  SubscriptionFeePricingStrategy,
  SubscriptionOptionEnum,
} from "@shared_packages/toolkit/types";
import {
  FEE_TYPE_ROWS,
  PO_BILLING_FEE_CURRENCY_CODES,
  bulkFixedMonthlyPlanLabel,
  feeRuleByType,
  formatMoneyFromPence,
  normalizeSchedule,
  paymentsPricingModeToWizardMode,
} from "./subscription-billing.helpers";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.button<{ $on: boolean }>`
  border: 1px solid ${({ $on }) => ($on ? "#027aff" : "#d1d5db")};
  background: ${({ $on }) => ($on ? "#eef6ff" : "#fff")};
  color: ${({ $on }) => ($on ? "#027aff" : "#374151")};
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  font-size: 13px;
  color: #495b6c;
`;

const MetaKey = styled.span`
  color: #888;
  margin-right: 4px;
`;

const CurrencyCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  border: 1px solid #eef2f7;
  border-radius: 8px;
  padding: 12px;
  background: #fff;
`;

const CurrencyHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const CurrencyTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #13273f;
`;

const RuleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RuleRow = styled.div`
  display: grid;
  grid-template-columns: minmax(130px, 0.9fr) minmax(0, 1.8fr);
  gap: 12px;
  padding: 10px 0;
  border-top: 1px solid #f1f5f9;
  &:first-child {
    border-top: none;
    padding-top: 0;
  }
`;

const TypeLabel = styled.div`
  font-weight: 600;
  color: #13273f;
`;

const RuleBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RuleMain = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #13273f;
`;

const RuleSub = styled.div`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.35;
`;

const RuleEstimate = styled.div`
  font-size: 12px;
  color: #13273f;
  font-weight: 600;
`;

const EmptyText = styled.span`
  color: #9ca3af;
`;

const SimpleTable = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
`;

const SimpleRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr 1fr;
  gap: 4px;
  padding: 8px 12px;
  font-size: 13px;
  border-bottom: 1px solid #f0f0f0;
  &:last-child {
    border-bottom: none;
  }
`;

const SimpleLabel = styled.div`
  color: #666;
  font-weight: 500;
`;

const SimpleNote = styled.div`
  color: #aaa;
  font-size: 11px;
`;

const SimpleValue = styled.div`
  font-weight: 600;
  color: #13273f;
  text-align: right;
`;

function modeLabel(mode: PaymentsPricingMode): string {
  switch (mode) {
    case PaymentsPricingMode.BESPOKE:
      return "Bespoke";
    case PaymentsPricingMode.MONTHLY_FEE:
      return "Monthly fee";
    case PaymentsPricingMode.RULE_BASED:
      return "Rule based";
    default:
      return String(mode);
  }
}

/**
 * EU Open Banking access is configured independently from pricing. Keep the
 * explicit subscription entitlement visible even when the pricing mode has no
 * per-currency fee schedules (for example Monthly fee or Bespoke).
 */
const EnabledPaymentCurrencies: FC<{
  currencies: string[] | null | undefined;
}> = ({ currencies }) => {
  const value = (currencies ?? [])
    .map((currency) => currency.toUpperCase())
    .join(", ");

  return (
    <SimpleTable>
      <SimpleRow>
        <SimpleLabel>Enabled payment currencies</SimpleLabel>
        <SimpleNote>EU Open Banking access</SimpleNote>
        <SimpleValue>{value || "Not configured"}</SimpleValue>
      </SimpleRow>
    </SimpleTable>
  );
};

type UsageSummary = {
  volumeMinor: number;
  unitCount: number;
};

function usageKey(currency: string, payableEntityType: string | null): string {
  return `${currency.toLowerCase()}::${payableEntityType ?? "default"}`;
}

function buildUsageMap(
  usageBuckets: SubscriptionUsageBucket[] | null | undefined,
  isTrialing: boolean
): Map<string, UsageSummary> {
  const map = new Map<string, UsageSummary>();
  for (const bucket of usageBuckets ?? []) {
    const currency = String(bucket.currency || "gbp").toLowerCase();
    const payableEntityType = bucket.payableEntityType ?? null;
    const key = usageKey(currency, payableEntityType);
    const current = map.get(key) ?? { volumeMinor: 0, unitCount: 0 };
    const bucketVolumeMinor = isTrialing
      ? 0
      : Math.max(
          0,
          (bucket.volumeMinor ?? bucket.levelUsed ?? 0) -
            (bucket.volumeMinorAtTrialEnd ?? bucket.levelUsedAtTrialEnd ?? 0)
        );
    const bucketUnitCount = isTrialing
      ? 0
      : Math.max(
          0,
          (bucket.unitCount ?? 0) - (bucket.unitCountAtTrialEnd ?? 0)
        );
    map.set(key, {
      volumeMinor: current.volumeMinor + Number(bucketVolumeMinor),
      unitCount: current.unitCount + Number(bucketUnitCount),
    });
  }
  return map;
}

function usageLabel(
  usage: UsageSummary | undefined,
  currency: string,
  rule: SubscriptionFeeSchedule["rules"][number] | undefined
): string {
  if (!rule || !usage) return "No billable usage this period";
  const count = usage.unitCount.toLocaleString("en-GB");
  if (rule.pricingStrategy === SubscriptionFeePricingStrategy.PER_SETUP_FEE) {
    return `${count} completed setups this period`;
  }
  if (
    rule.pricingStrategy === SubscriptionFeePricingStrategy.FIXED_MONTHLY_PLAN
  ) {
    const limit = rule.monthlyUnitLimit;
    return limit != null && limit > 0
      ? `${count} of ${limit.toLocaleString("en-GB")} transactions this period`
      : `${count} transactions this period`;
  }
  const parts: string[] = [];
  if (
    rule.pricingStrategy === SubscriptionFeePricingStrategy.STANDARD_TIERED ||
    rule.pricingStrategy === SubscriptionFeePricingStrategy.HIGH_RISK_TIERED ||
    (rule.percentageFee ?? 0) > 0
  ) {
    parts.push(
      `${formatMoneyFromPence(usage.volumeMinor, currency)} billable volume`
    );
  }
  if ((rule.flatFeeMinor ?? 0) > 0) {
    parts.push(`${count} billable transactions`);
  }
  return parts.length ? parts.join(" · ") : "No billable usage this period";
}

function estimatedRuleFee(
  preview: UpcomingInvoicePreview | null | undefined,
  currency: string,
  payableEntityType: string,
  invoiceCurrency: string
): string {
  if (
    !preview?.available ||
    !preview.estimatedFees ||
    !preview.estimatedMinimums
  ) {
    return "—";
  }
  const fee = preview.estimatedFees.find(
    (entry) =>
      entry.sourceCurrency.toLowerCase() === currency &&
      entry.payableEntityType === payableEntityType
  );
  if (
    !fee &&
    preview.estimatedMinimums.some(
      (entry) => entry.sourceCurrency.toLowerCase() === currency
    )
  ) {
    return "Covered by monthly minimum";
  }
  return formatMoneyFromPence(fee?.amountMinor ?? 0, invoiceCurrency);
}

function estimatedMinimumFee(
  preview: UpcomingInvoicePreview | null | undefined,
  currency: string,
  invoiceCurrency: string
): string {
  if (!preview?.available || !preview.estimatedMinimums) return "—";
  const minimum = preview.estimatedMinimums.find(
    (entry) => entry.sourceCurrency.toLowerCase() === currency
  );
  return formatMoneyFromPence(minimum?.amountMinor ?? 0, invoiceCurrency);
}

export const TransactionFeesScheduleViewer: FC<{
  fees: SubscriptionFeeSchedule[] | null | undefined;
  usageBuckets?: SubscriptionUsageBucket[] | null;
  interimBillingInterval?: string | null;
  upcomingInvoice?: UpcomingInvoicePreview | null;
  subscriptionCurrency?: string | null;
  isTrialing?: boolean;
}> = ({
  fees,
  usageBuckets,
  interimBillingInterval,
  upcomingInvoice,
  subscriptionCurrency,
  isTrialing = false,
}) => {
  const schedules = useMemo(() => {
    if (!fees?.length) return [];
    return normalizeSchedule(fees);
  }, [fees]);

  const usageMap = useMemo(
    () => buildUsageMap(usageBuckets, isTrialing),
    [usageBuckets, isTrialing]
  );
  const invoiceCurrency = String(
    subscriptionCurrency || upcomingInvoice?.currency || "gbp"
  ).toLowerCase();

  const currencies = useMemo(() => {
    const present = new Set(schedules.map((s) => s.currency));
    const ordered = PO_BILLING_FEE_CURRENCY_CODES.map((c) =>
      c.toLowerCase()
    ).filter((c) => present.has(c));
    for (const s of schedules) {
      if (!ordered.includes(s.currency)) ordered.push(s.currency);
    }
    return ordered;
  }, [schedules]);

  const [selected, setSelected] = useState(currencies[0] ?? "gbp");

  useEffect(() => {
    if (!currencies.length) return;
    if (!currencies.includes(selected)) setSelected(currencies[0]);
  }, [currencies, selected]);

  const schedule = schedules.find((s) => s.currency === selected);
  const rows =
    selected === "gbp"
      ? FEE_TYPE_ROWS
      : FEE_TYPE_ROWS.filter((row) => row.type === PayableEntityType.PAYMENT);

  if (!schedules.length || !schedule) {
    return (
      <SimpleTable>
        <SimpleRow>
          <SimpleLabel>Pricing type</SimpleLabel>
          <SimpleNote>custom fees configured without schedule</SimpleNote>
          <SimpleValue>Custom fees</SimpleValue>
        </SimpleRow>
      </SimpleTable>
    );
  }

  return (
    <Wrap>
      <ChipRow>
        {currencies.map((code) => (
          <Chip
            key={code}
            type="button"
            $on={selected === code}
            onClick={() => setSelected(code)}
          >
            {code.toUpperCase()}
          </Chip>
        ))}
      </ChipRow>

      <CurrencyCard>
        <CurrencyHeader>
          <CurrencyTitle>{selected.toUpperCase()} usage</CurrencyTitle>
          <MetaRow>
            <div>
              <MetaKey>Invoice currency</MetaKey>
              {invoiceCurrency.toUpperCase()}
            </div>
            <div>
              <MetaKey>Estimates</MetaKey>
              Before VAT and discounts
            </div>
            {interimBillingInterval && (
              <div>
                <MetaKey>Interim</MetaKey>
                <span style={{ textTransform: "capitalize" }}>
                  {interimBillingInterval}
                </span>
              </div>
            )}
          </MetaRow>
        </CurrencyHeader>

        <RuleList>
          {rows.map((row) => {
            const rule = feeRuleByType(schedule, row.type);
            const flat = rule?.flatFeeMinor;
            const pct = rule?.percentageFee;
            const usage = usageMap.get(usageKey(selected, row.type));
            const standardTiered =
              rule?.pricingStrategy ===
              SubscriptionFeePricingStrategy.STANDARD_TIERED;
            const highRiskTiered =
              rule?.pricingStrategy ===
              SubscriptionFeePricingStrategy.HIGH_RISK_TIERED;
            const fixedMonthlyPlan =
              rule?.pricingStrategy ===
              SubscriptionFeePricingStrategy.FIXED_MONTHLY_PLAN;
            const perSetupFee =
              rule?.pricingStrategy ===
              SubscriptionFeePricingStrategy.PER_SETUP_FEE;
            const fixedMonthlyLabel =
              rule?.monthlyPlanKey != null
                ? bulkFixedMonthlyPlanLabel(rule.monthlyPlanKey)
                : rule?.monthlyFeeMinor != null &&
                  rule?.monthlyUnitLimit != null
                ? `${formatMoneyFromPence(
                    rule.monthlyFeeMinor,
                    selected
                  )}/mo · up to ${rule.monthlyUnitLimit.toLocaleString(
                    "en-GB"
                  )} tx`
                : null;
            const customParts = [
              flat != null
                ? `${formatMoneyFromPence(flat, selected)} / ${
                    perSetupFee ? "setup" : "tx"
                  }`
                : null,
              !fixedMonthlyPlan && !perSetupFee && pct != null
                ? `${pct}% of volume`
                : null,
            ].filter(Boolean);
            const usageText = usageLabel(usage, selected, rule);
            const estimatedFee = rule
              ? estimatedRuleFee(
                  upcomingInvoice,
                  selected,
                  row.type,
                  invoiceCurrency
                )
              : "—";
            const mainText = standardTiered
              ? "Standard tiered pricing"
              : highRiskTiered
              ? "High-risk tiered pricing"
              : fixedMonthlyPlan && fixedMonthlyLabel
              ? `Fixed monthly plan · ${fixedMonthlyLabel}`
              : perSetupFee && customParts.length
              ? `Per setup fee · ${customParts.join(" · ")}`
              : customParts.length
              ? `Custom fees · ${customParts.join(" · ")}`
              : null;

            return (
              <RuleRow key={row.type}>
                <TypeLabel>{row.label}</TypeLabel>
                <RuleBody>
                  <RuleMain>
                    {mainText ?? <EmptyText>Not configured</EmptyText>}
                  </RuleMain>
                  <RuleSub>{usageText}</RuleSub>
                  {rule && (
                    <RuleEstimate>
                      Est. fee this period: {estimatedFee}
                    </RuleEstimate>
                  )}
                </RuleBody>
              </RuleRow>
            );
          })}
          {(schedule.minimumMonthlyFeeMinor ?? 0) > 0 && (
            <RuleRow>
              <TypeLabel>Monthly minimum</TypeLabel>
              <RuleBody>
                <RuleMain>
                  {formatMoneyFromPence(
                    schedule.minimumMonthlyFeeMinor as number,
                    selected
                  )}
                  {" / month · VAT exempt"}
                </RuleMain>
                <RuleSub>
                  Applies across custom fees in {selected.toUpperCase()}
                </RuleSub>
                <RuleEstimate>
                  Est. fee this period:{" "}
                  {estimatedMinimumFee(
                    upcomingInvoice,
                    selected,
                    invoiceCurrency
                  )}
                </RuleEstimate>
              </RuleBody>
            </RuleRow>
          )}
        </RuleList>
      </CurrencyCard>
    </Wrap>
  );
};

/** Payments-Only pricing block driven only by the active subscription entry. */
export const SubscriptionPricingBreakdown: FC<{
  sub: CompanySubscriptionDetailsEntry;
}> = ({ sub }) => {
  const isEuPaymentsSubscription =
    sub.subscriptionOption === SubscriptionOptionEnum.PAYMENTS_ONLY_EU;
  const enabledPaymentCurrencies = isEuPaymentsSubscription ? (
    <EnabledPaymentCurrencies currencies={sub.enabledPaymentCurrencies} />
  ) : null;
  const mode = paymentsPricingModeToWizardMode(sub.paymentsPricingMode, {
    customFlatFeePerTransaction: sub.customFlatFeePerTransaction,
    customPercentageFee: sub.customPercentageFee,
    customPercentageFeeMinimum: sub.customPercentageFeeMinimum,
    hasBespokePricing: sub.hasBespokePricing,
    hasHighRiskSubscriptionPricing: sub.hasHighRiskSubscriptionPricing,
  });

  if (mode === PaymentsPricingMode.BESPOKE) {
    return (
      <Wrap>
        {enabledPaymentCurrencies}
        <SimpleTable>
          <SimpleRow>
            <SimpleLabel>Pricing type</SimpleLabel>
            <SimpleNote>managed by account team</SimpleNote>
            <SimpleValue>Bespoke</SimpleValue>
          </SimpleRow>
        </SimpleTable>
      </Wrap>
    );
  }

  if (mode === PaymentsPricingMode.MONTHLY_FEE) {
    const invoiceCurrency = String(
      sub.currency || sub.upcomingInvoice?.currency || "gbp"
    ).toLowerCase();
    const estimatedMonthlyFee =
      sub.upcomingInvoice?.available &&
      sub.upcomingInvoice.estimatedMonthlyFeeMinor != null
        ? formatMoneyFromPence(
            sub.upcomingInvoice.estimatedMonthlyFeeMinor,
            invoiceCurrency
          )
        : "—";
    return (
      <Wrap>
        {enabledPaymentCurrencies}
        <SimpleTable>
          <SimpleRow>
            <SimpleLabel>Pricing type</SimpleLabel>
            <SimpleNote>fixed fee in the subscription currency</SimpleNote>
            <SimpleValue>
              {sub.monthlyFeeMinor != null
                ? `${formatMoneyFromPence(
                    sub.monthlyFeeMinor,
                    sub.currency ?? "gbp"
                  )} / month`
                : "Not configured"}
            </SimpleValue>
          </SimpleRow>
          <SimpleRow>
            <SimpleLabel>Est. fee this period</SimpleLabel>
            <SimpleNote>before VAT and discounts</SimpleNote>
            <SimpleValue>{estimatedMonthlyFee}</SimpleValue>
          </SimpleRow>
        </SimpleTable>
      </Wrap>
    );
  }

  if (mode === PaymentsPricingMode.RULE_BASED) {
    return (
      <Wrap>
        {enabledPaymentCurrencies}
        <MetaRow>
          <div>
            <MetaKey>Mode</MetaKey>
            {modeLabel(mode)}
          </div>
        </MetaRow>
        <TransactionFeesScheduleViewer
          fees={sub.feeSchedules}
          usageBuckets={sub.usageBuckets}
          interimBillingInterval={sub.interimBillingInterval}
          upcomingInvoice={sub.upcomingInvoice}
          subscriptionCurrency={sub.currency}
          isTrialing={sub.status === "trialing"}
        />
      </Wrap>
    );
  }

  return (
    <SimpleTable>
      <SimpleRow>
        <SimpleLabel>Pricing type</SimpleLabel>
        <SimpleNote>no custom overrides applied</SimpleNote>
        <SimpleValue>Standard</SimpleValue>
      </SimpleRow>
      {sub.interimBillingInterval && (
        <SimpleRow>
          <SimpleLabel>Interim billing</SimpleLabel>
          <SimpleNote>charges billed throughout the period</SimpleNote>
          <SimpleValue style={{ textTransform: "capitalize" }}>
            {sub.interimBillingInterval}
          </SimpleValue>
        </SimpleRow>
      )}
    </SimpleTable>
  );
};
